import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React from "react";
import { styled, styledRN } from "./styled";
import { Spinner } from "./spinner";
import { LoadingSpinner } from "./loading-spinner";
import { HiylloBG } from "./hiyllo-bg-provider";
import { Platform, Pressable, Text, View } from "react-native";
import { useFontAwesomeIcon } from "./platform";
import { useTheme } from "./theme";
import { motion, useAnimationControls } from "framer-motion";
import { faCheck } from "@fortawesome/pro-light-svg-icons";
import { MouseInteraction, useMouseState } from "./animation";
import { Icon } from "./icon";
const layoutSizingStyles = {
    paddingLeft: 15,
    paddingRight: 15,
    height: 40,
    borderRadius: 10,
    display: 'flex',
    flexDirection: "row",
    gap: 5,
    justifyContent: 'center',
    alignItems: 'center',
};
const TEXT_STYLES = {
    fontSize: 15,
    fontFamily: 'hiyllo',
    color: 'white',
};
const sharedStyles = {
    ...layoutSizingStyles,
    ...TEXT_STYLES,
};
const sharedStylesWeb = {
    ...sharedStyles,
    userSelect: 'none',
};
const OutlineButtonContainer = styled('div', ({ $theme }) => ({
    ...sharedStylesWeb,
    paddingLeft: 15 - 0.75,
    paddingRight: 15 - 0.75,
    height: 40 - (0.75 * 2),
    borderWidth: 0.75,
    borderStyle: 'solid',
    borderColor: $theme.foregroundInactive,
    color: $theme.foregroundInactive,
    cursor: 'pointer',
}));
export const BUTTON_STYLES = {
    layoutSizingStyles,
    TEXT_STYLES,
    sharedStyles
};
export const OutlineButton = React.memo(function OutlineButton(props) {
    return (_jsx(OutlineButtonContainer, { ...props, children: props.isLoading ? _jsx(Spinner, {})
            : props.children }));
});
export var ButtonVariant;
(function (ButtonVariant) {
    ButtonVariant["narrow"] = "narrow";
})(ButtonVariant || (ButtonVariant = {}));
const ButtonElement = styled("div", ({ $theme, isLoading, isSecondary, $variant, $autoWidth }) => ({
    ...sharedStylesWeb,
    background: isSecondary ? $theme.midground : $theme.buttonBackground,
    color: isSecondary ? $theme.foreground : $theme.buttonForeground,
    border: "none",
    cursor: isLoading ? "" : "pointer",
    position: "relative",
    width: $autoWidth === true ? "fit-content" : undefined,
    paddingLeft: 0,
    paddingRight: 0,
    ...($variant === ButtonVariant.narrow ? {
        height: 30,
        borderRadius: 5,
    } : {})
}));
export const ButtonInnerElement = styled("div", ({ $variant, $isLoading }) => ({
    opacity: $isLoading ? 0 : 1,
    whiteSpace: 'nowrap',
    ...layoutSizingStyles,
    ...($variant === ButtonVariant.narrow ? {
        paddingLeft: 10,
        paddingRight: 10,
        fontSize: 12.5,
    } : {})
}));
const SuccessWeb = React.memo(function SuccessWeb(props) {
    const FontAwesomeIcon = useFontAwesomeIcon();
    const $theme = useTheme();
    const animate = useAnimationControls();
    React.useEffect(() => {
        void animate.start({
            width: "100%",
            transition: {
                ease: [0.1, 0.12, 0.1, 0.95],
                duration: 0.4
            }
        }).then(() => {
            void animate.start({
                backgroundColor: $theme[props.inactiveColor ?? "midground"],
                color: "#4caf50",
                transition: {
                    ease: [0.1, 0.12, 0.1, 0.95],
                    duration: 0.4
                }
            });
        });
    }, []);
    return (_jsx(motion.div, { animate: animate, initial: { width: "0%" }, style: {
            position: 'absolute',
            top: 0,
            left: 0,
            borderRadius: 10,
            height: '100%',
            backgroundColor: '#4caf50',
        }, transition: {
            ease: [0.1, 0.12, 0.1, 0.95],
            duration: 0.3
        }, children: _jsxs(motion.div, { style: {
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "center",
                height: "100%",
                width: "100%",
                gap: 5,
                fontSize: 15,
                fontFamily: 'hiyllo',
            }, initial: { opacity: 0 }, animate: { opacity: 1 }, transition: {
                ease: [0.1, 0.12, 0.1, 0.95],
                duration: 0.3,
                delay: 0.15
            }, children: [_jsx(FontAwesomeIcon, { icon: faCheck }), props.successMessage] }) }));
});
const SuccessNative = React.memo(function SuccessNative(props) {
    const FontAwesomeIcon = useFontAwesomeIcon();
    return (_jsx(View, { style: {
            position: 'absolute',
            top: 0,
            left: 0,
            borderRadius: 10,
            height: '100%',
            width: '100%',
            backgroundColor: '#4caf50',
            zIndex: 10
        }, children: _jsxs(View, { style: {
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "center",
                height: "100%",
                width: "100%",
                gap: 5,
            }, children: [_jsx(FontAwesomeIcon, { icon: faCheck, color: "white" }), _jsx(Text, { style: {
                        fontSize: 15,
                        fontFamily: 'hiyllo',
                        color: 'white',
                    }, children: props.successMessage })] }) }));
});
export var MouseState;
(function (MouseState) {
    MouseState["none"] = "none";
    MouseState["hovered"] = "hovered";
    MouseState["pressed"] = "pressed";
})(MouseState || (MouseState = {}));
export const ButtonWeb = React.memo(function ButtonWeb(props) {
    const { onMouseEnter, onMouseLeave, onMouseDown, onMouseUp, mouseState } = useMouseState();
    const isLoading = props.isLoading === true;
    const onClick = React.useCallback((evt) => {
        evt.preventDefault();
        if (props.onClick != null)
            props.onClick(evt);
    }, [props]);
    return (_jsxs(ButtonElement, { id: props.id, onClick: onClick, disabled: isLoading, style: props.style, isSecondary: props.isSecondary, "$variant": props.variant, onMouseEnter: onMouseEnter, onMouseLeave: onMouseLeave, onMouseDown: onMouseDown, onMouseUp: onMouseUp, "$autoWidth": props.autoWidth, children: [_jsx(MouseInteraction, { factorShift: 1.25, children: _jsxs(ButtonInnerElement, { "$isLoading": isLoading, "$variant": props.variant, children: [props.icon ?
                            _jsx(Icon, { icon: props.icon, color: "white", width: props.variant === ButtonVariant.narrow ? 12.5 : 15 })
                            : null, props.label] }) }), isLoading ?
                _jsx("div", { style: { position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, height: '100%', width: '100%', zIndex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center' }, children: _jsx(LoadingSpinner, {}) })
                : null, props.success ?
                _jsx(SuccessWeb, { successMessage: props.successMessage, inactiveColor: props.inactiveColor })
                : null] }));
});
const MidgroundRN = styledRN(View, ({ $theme }) => ({
    backgroundColor: $theme.midground,
}));
export const buttonInnerElementRNStyles = ({ $variant }) => ({
    ...BUTTON_STYLES.layoutSizingStyles,
    ...($variant === ButtonVariant.narrow ? {
        paddingLeft: 10,
        paddingRight: 10,
        height: 30,
        borderRadius: 5,
    } : {})
});
const ButtonRN = React.memo(function Button(props) {
    const textStyles = React.useMemo(() => {
        return {
            ...BUTTON_STYLES.TEXT_STYLES,
            opacity: props.isLoading === true ? 0 : 1,
            fontSize: props.variant === ButtonVariant.narrow ? 12.5 : BUTTON_STYLES.TEXT_STYLES.fontSize,
        };
    }, [props.isLoading]);
    const BGElem = props.isSecondary === true ? MidgroundRN : HiylloBG;
    return (_jsx(View, { style: props.autoWidth ? { display: 'flex', flexDirection: 'row' } : {}, children: _jsxs(Pressable, { onPress: props.onClick, children: [_jsx(BGElem, { style: { borderRadius: buttonInnerElementRNStyles({ $variant: props.variant }).borderRadius }, children: _jsxs(View, { style: buttonInnerElementRNStyles({ $variant: props.variant }), children: [props.icon ?
                                _jsx(Icon, { icon: props.icon, color: "white", width: props.variant === ButtonVariant.narrow ? 12.5 : 15 })
                                : null, _jsx(Text, { style: textStyles, children: props.label })] }) }), props.isLoading === true ?
                    _jsx(View, { style: { position: 'absolute', top: 0, left: 0, height: '100%', width: '100%', display: 'flex', justifyContent: 'center', alignItems: "center" }, children: _jsx(LoadingSpinner, {}) })
                    : null, props.success ?
                    _jsx(SuccessNative, { successMessage: props.successMessage, inactiveColor: props.inactiveColor })
                    : null] }) }));
});
export const Button = Platform.OS === 'web' ? ButtonWeb : ButtonRN;
export const DangerButton = React.memo(function DangerButton(props) {
    const [isConfirming, setIsConfirming] = React.useState(false);
    const onClickInner = React.useCallback((evt) => {
        if (isConfirming) {
            props.onClick?.(evt);
        }
        else {
            setIsConfirming(true);
            setTimeout(() => {
                setIsConfirming(false);
            }, 5000);
        }
    }, [isConfirming, props.onClick]);
    return (_jsx("div", { style: { display: "flex" }, children: _jsx(ButtonWeb, { isSecondary: true, onClick: onClickInner, label: isConfirming ? 'Click again to confirm: ' + props.label : props.label, style: isConfirming ? { background: 'red' } : {} }) }));
});
